import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ResearchPaperDTO, ScreeningResultDTO} from '../../dto/research-paper';
import {environment} from '../../../../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {getHeadersEventSource} from '../../../../utils/interceptors/http-header.interceptor';
import {DeviceIdService} from '../../../../utils/services/device-id.service';
import {SseClient} from 'ngx-sse-client';
import {map} from 'rxjs/operators';
import {ScreeningRunSettings} from '../../models/screening-settings';

@Injectable({
  providedIn: 'root',
})
export class ScreeningService {
  private readonly sseClient: SseClient = inject(SseClient);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly deviceIdService: DeviceIdService = inject(DeviceIdService);
  private readonly apiUrl = environment.apiBaseUrl + '/project';

  overrideScreening(projectId: number, runId: number, screeningOne: boolean, screeningResul: ScreeningResultDTO): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!runId) {
      throw new Error('runId is required');
    }
    const params = new HttpParams().set('screeningOne', screeningOne.toString());
    return this.http.put<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/screenings/run/${runId}`, screeningResul, {params});
  }

  deleteScreeningRun(projectId: number, processId: number): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!processId) {
      throw new Error('processId is required');
    }
    return this.http.delete<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/screenings/process/${processId}`);
  }

  isScreeningRunning(projectId: number): Observable<boolean> {
    const url = `${this.apiUrl}/${projectId}/screenings/running`;
    return this.http.get(url, {observe: 'response'}).pipe(
      map(response => response.status === 200)
    );
  }

  stopScreening(projectId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${projectId}/screenings`);
  }

  startScreening(projectId: number, screeningRunSettings?: ScreeningRunSettings): Observable<ResearchPaperDTO> {
    const headers = getHeadersEventSource(this.deviceIdService.getDeviceId());
    let params: HttpParams = new HttpParams();
    if (screeningRunSettings && screeningRunSettings.ignoreScreened) {
      params = params.set('ignoreScreened', true);
    }
    if (screeningRunSettings && screeningRunSettings.processFullText) {
      params = params.set('processFullText', true);
    }
    if (screeningRunSettings && screeningRunSettings.excludePapers) {
      params = params.set('excludePapers', screeningRunSettings.excludePapers.map(filter => filter.toString()).join(','));
    }
    if (screeningRunSettings && screeningRunSettings.minScore) {
      params = params.set('minScore', screeningRunSettings.minScore);
    }
    if (screeningRunSettings && screeningRunSettings.maxResults) {
      params = params.set('maxResults', screeningRunSettings.maxResults);
    }
    return this.sseClient.stream(
      `${this.apiUrl}/${projectId}/screenings`,
      {keepAlive: false},
      {headers, params},
      'POST'
    ).pipe(map(event => this.toPaper(event)));
  }

  processScreeningStream(projectId: number): Observable<ResearchPaperDTO> {
    const headers = getHeadersEventSource(this.deviceIdService.getDeviceId());
    return this.sseClient.stream(
      `${this.apiUrl}/${projectId}/screenings`,
      {keepAlive: false},
      {headers},
      'GET'
    ).pipe(map(event => this.toPaper(event)));
  }

  private toPaper(event: Event): ResearchPaperDTO {
    if (event.type === 'error') {
      const errorEvent = event as ErrorEvent;
      console.error(errorEvent.error, errorEvent.message);
      throw new Error('SSE connection error');
    }
    const messageEvent = event as MessageEvent;
    return JSON.parse(messageEvent.data) as ResearchPaperDTO;
  }

  processScreeningStreamOnePaper(projectId: number, paperId: number): Observable<ResearchPaperDTO> {
    const headers = getHeadersEventSource(this.deviceIdService.getDeviceId());
    return this.sseClient.stream(
      `${this.apiUrl}/${projectId}/screenings/paper/${paperId}`,
      {keepAlive: false},
      {headers},
      'GET'
    ).pipe(
      map(event => {
        if (event.type === 'error') {
          const errorEvent = event as ErrorEvent;
          console.error(errorEvent.error, errorEvent.message);
          throw new Error('SSE connection error');
        }
        const messageEvent = event as MessageEvent;
        return JSON.parse(messageEvent.data) as ResearchPaperDTO;
      })
    );
  }


}

